import React, { useState } from "react";
import { CreditCard, Plus, Trash2, CheckCircle2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard, ccBtnPink, EmptyState } from "../care/ui";

const STATUS_STYLE = {
  pending: "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]",
  paid: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]",
  overdue: "bg-rose-50 border-rose-200 text-rose-500",
};

// Invoicing engine: ledger summary, full CRUD, recurring monthly generation
export default function Invoices({ ctx }) {
  const [showForm, setShowForm] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ childId: "", period: "", dueDate: "", notes: "", items: [{ uid: crypto.randomUUID(), label: "Monthly Tuition", amount: "" }] });
  const [genForm, setGenForm] = useState({ period: new Date().toISOString().slice(0, 7), amount: "", dueDate: "" });
  const invoices = ctx.invoices || [];
  const children = ctx.allChildren || [];

  const billed = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const collected = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);
  const outstanding = billed - collected;

  const childName = (id) => {
    const c = children.find((x) => x.id === id);
    return c ? `${c.firstName} ${c.lastName}` : id;
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await ctx.ws.addInvoice({
        ...form,
        childName: childName(form.childId),
        items: form.items.filter((i) => i.label && i.amount).map((i) => ({ label: i.label, amount: parseFloat(i.amount) })),
      });
      toast.success("Invoice created.");
      setShowForm(false);
      setForm({ childId: "", period: "", dueDate: "", notes: "", items: [{ uid: crypto.randomUUID(), label: "Monthly Tuition", amount: "" }] });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    try {
      const res = await ctx.ws.generateMonthlyInvoices({ period: genForm.period, amount: parseFloat(genForm.amount) || 0, dueDate: genForm.dueDate });
      toast.success(`${res.created} invoices generated for ${genForm.period}.`);
      setShowGenerate(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const markPaid = async (inv) => {
    try {
      await ctx.ws.updateInvoice(inv.id, { status: inv.status === "paid" ? "pending" : "paid" });
      toast.success(inv.status === "paid" ? "Invoice reopened." : "Invoice marked paid.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (iid) => {
    try {
      await ctx.ws.deleteInvoice(iid);
      toast.success("Invoice deleted.");
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="billing-panel">
      {/* Ledger summary */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="billing-ledger">
        {[
          ["Total Billed", billed, "ledger-billed", "bg-[#E6F2FA] text-[#3D7E9E]"],
          ["Collected", collected, "ledger-collected", "bg-[#E2F4E1] text-[#2F6031]"],
          ["Outstanding", outstanding, "ledger-outstanding", "bg-[#FFF2D1] text-[#A08244]"],
        ].map(([label, val, tid, cls]) => (
          <div key={tid} className={`p-4 rounded-2xl border border-white/60 ${cls.split(" ")[0]}`} data-testid={tid}>
            <p className="text-lg font-black font-heading">${val.toFixed(2)}</p>
            <p className={`text-[10px] font-extrabold uppercase tracking-wide ${cls.split(" ")[1]}`}>{label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setShowForm((v) => !v)} data-testid="invoice-add-btn" className={ccBtnPink}>
          <Plus className="w-4 h-4 inline mr-1 -mt-0.5" /> New Invoice
        </button>
        <button onClick={() => setShowGenerate((v) => !v)} data-testid="invoice-generate-btn" className="flex items-center gap-1.5 px-4 py-2.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-xs font-bold transition-all">
          <RefreshCw className="w-3.5 h-3.5" /> Generate Monthly Invoices
        </button>
      </div>

      {showGenerate && (
        <form onSubmit={generate} className="mb-5 p-5 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="invoice-generate-form">
          <div>
            <label className={ccLabel}>Period</label>
            <input type="month" required value={genForm.period} onChange={(e) => setGenForm({ ...genForm, period: e.target.value })} className={ccInput} data-testid="generate-period-input" />
          </div>
          <div>
            <label className={ccLabel}>Tuition per Child ($)</label>
            <input type="number" min="0" step="0.01" required value={genForm.amount} onChange={(e) => setGenForm({ ...genForm, amount: e.target.value })} className={ccInput} data-testid="generate-amount-input" />
          </div>
          <div>
            <label className={ccLabel}>Due Date</label>
            <input type="date" value={genForm.dueDate} onChange={(e) => setGenForm({ ...genForm, dueDate: e.target.value })} className={ccInput} data-testid="generate-due-input" />
          </div>
          <button type="submit" className={ccBtnPink} data-testid="generate-submit-btn">Generate</button>
        </form>
      )}

      {showForm && (
        <form onSubmit={save} className="mb-5 p-5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl space-y-3" data-testid="invoice-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={ccLabel}>Child</label>
              <select required value={form.childId} onChange={(e) => setForm({ ...form, childId: e.target.value })} className={ccInput} data-testid="invoice-child-select">
                <option value="">Select child…</option>
                {children.map((c) => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={ccLabel}>Period</label>
              <input type="month" required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className={ccInput} data-testid="invoice-period-input" />
            </div>
            <div>
              <label className={ccLabel}>Due Date</label>
              <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className={ccInput} data-testid="invoice-due-input" />
            </div>
          </div>
          {form.items.map((item, i) => (
            <div key={item.uid} className="flex gap-2 items-center">
              <input value={item.label} onChange={(e) => setForm({ ...form, items: form.items.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)) })} className={ccInput} placeholder="Item" data-testid={`invoice-item-label-${i}`} />
              <input type="number" min="0" step="0.01" value={item.amount} onChange={(e) => setForm({ ...form, items: form.items.map((x, xi) => (xi === i ? { ...x, amount: e.target.value } : x)) })} className={ccInput} placeholder="Amount" data-testid={`invoice-item-amount-${i}`} />
              {form.items.length > 1 && (
                <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, xi) => xi !== i) })} className="text-[#9CA3AF] hover:text-rose-400" data-testid={`invoice-item-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { uid: crypto.randomUUID(), label: "", amount: "" }] })} className="text-[11px] font-bold text-[#5CB3DC] hover:underline" data-testid="invoice-item-add">
            + Add line item
          </button>
          <div>
            <button type="submit" className={ccBtnPink} data-testid="invoice-save-btn">Create Invoice</button>
          </div>
        </form>
      )}

      {invoices.length === 0 ? (
        <EmptyState text="No invoices yet — create one or generate monthly tuition for all enrolled children." testId="invoices-empty" />
      ) : (
        <div className="space-y-2.5">
          {invoices.map((inv) => (
            <div key={inv.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap" data-testid={`invoice-row-${inv.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-extrabold text-[#374151]">
                  {inv.childName || childName(inv.childId)} · ${(inv.amount || 0).toFixed(2)}
                </p>
                <p className="text-[11px] text-[#9CA3AF] font-medium">
                  {inv.period} {inv.dueDate && `· due ${inv.dueDate}`} · {(inv.items || []).map((i) => i.label).join(", ")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${STATUS_STYLE[inv.status] || STATUS_STYLE.pending}`} data-testid={`invoice-status-${inv.id}`}>
                  {inv.status}
                </span>
                <button onClick={() => markPaid(inv)} data-testid={`invoice-mark-paid-${inv.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[10px] font-bold hover:bg-[#D2EBD1] transition-colors">
                  <CheckCircle2 className="w-3 h-3" /> {inv.status === "paid" ? "Reopen" : "Mark Paid"}
                </button>
                {confirmDelete === inv.id ? (
                  <button onClick={() => remove(inv.id)} data-testid={`invoice-delete-confirm-${inv.id}`} className="px-2.5 py-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full">Confirm?</button>
                ) : (
                  <button onClick={() => setConfirmDelete(inv.id)} data-testid={`invoice-delete-${inv.id}`} className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-rose-400 hover:bg-rose-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
